/**
 * StorageScopeProvider — sets the active storage scope based on pubkey.
 * Wrap this around the app below the auth provider. When the pubkey changes,
 * all hooks re-render and read from the correct scoped storage.
 */

import { createContext, use, useMemo } from 'react';
import { type StorageScope, setActiveScope } from '@/lib/scoped-storage';

const StorageScopeContext = createContext<StorageScope>({ type: 'guest' });

export function StorageScopeProvider({
  pubkey,
  children,
}: {
  pubkey: string | null;
  children: React.ReactNode;
}) {
  const scope: StorageScope = useMemo(
    () => (pubkey ? { type: 'user', pubkey } : { type: 'guest' }),
    [pubkey],
  );

  // Set immediately on render (before effects run) so first reads are correct.
  // This also keeps the module-level scope in sync for non-React code like sync.ts.
  setActiveScope(scope);

  return (
    <StorageScopeContext.Provider value={scope}>
      {children}
    </StorageScopeContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useStorageScope(): StorageScope {
  return use(StorageScopeContext);
}
